import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslations } from './hooks/useTranslations';

// Calendly Integration Component
const CalendlyButton = ({ 
  text = "Reserva tu sesión", 
  variant = "primary", 
  size = "medium",
  className = "",
  showMicrocopy = true,
  icon = "📅"
}) => {
  const { t } = useTranslations();
  const calendlyUrl = "https://calendly.com/tamaranavarretenavas/asesoria-your-brand-value?utm_source=ybv&utm_medium=site&utm_campaign=free_session";
  
  const baseClasses = "inline-flex items-center justify-center font-semibold rounded-xl transition-all duration-300 transform hover:scale-105 text-center";
  
  const variants = {
    primary: "bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white shadow-lg",
    secondary: "border-2 border-green-500 text-green-400 hover:bg-green-500/10 hover:text-green-300",
    outline: "border-2 border-white/20 text-white hover:bg-white/10 hover:border-white/40",
    accent: "bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600 text-white shadow-lg"
  };
  
  const sizes = {
    small: "px-4 py-2 text-sm",
    medium: "px-6 py-3 text-base",
    large: "px-8 py-4 text-lg"
  };
  
  const buttonClasses = `${baseClasses} ${variants[variant]} ${sizes[size]} ${className}`;
  
  return (
    <div className="text-center">
      <a
        href={calendlyUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={buttonClasses}
        aria-label="Reservar sesión gratuita de 30 minutos con Tamara Navarrete"
      >
        <span className="mr-2">{icon}</span>
        {text}
      </a>
      {showMicrocopy && (
        <p className="text-sm text-gray-400 mt-2">
          {t('advisory.freeSession.text')}
        </p>
      )}
    </div>
  );
};

const Navigation = () => {
  const { t } = useTranslations();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-900/90 backdrop-blur-sm border-b border-white/10 w-full">
      <div className="w-full px-3 sm:px-4 lg:px-8">
        <div className="flex items-center justify-between h-16 w-full">
          <Link to="/" className="text-lg sm:text-xl lg:text-2xl font-bold text-white flex-shrink-0" onClick={closeMobileMenu}>
            Your<span className="text-purple-400">Brand</span>Value
          </Link>
          
          <div className="hidden md:flex space-x-4 lg:space-x-8 flex-shrink-0">
            <Link to="/" className="text-gray-300 hover:text-white transition-colors text-sm lg:text-base">{t('navigation.home')}</Link>
            <Link to="/sobre-mi" className="text-purple-400 hover:text-purple-300 transition-colors text-sm lg:text-base font-semibold">{t('navigation.aboutMe')}</Link>
            <Link to="/metodologia" className="text-gray-300 hover:text-white transition-colors text-sm lg:text-base">{t('navigation.methodology')}</Link>
            <Link to="/casos-estudio" className="text-gray-300 hover:text-white transition-colors text-sm lg:text-base">{t('navigation.caseStudies')}</Link>
            <Link to="/asesoria" className="text-gray-300 hover:text-white transition-colors text-sm lg:text-base">{t('navigation.consulting')}</Link>
            <Link to="/contacto" className="text-gray-300 hover:text-white transition-colors text-sm lg:text-base">{t('navigation.contact')}</Link>
          </div>

          <div className="md:hidden flex-shrink-0">
            <button
              onClick={toggleMobileMenu}
              className="text-gray-300 hover:text-white p-2 transition-colors"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {isMobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {isMobileMenuOpen && (
          <div className="md:hidden bg-slate-800/95 backdrop-blur-sm border-t border-white/10">
            <div className="px-4 pt-2 pb-3 space-y-1">
              <Link to="/" className="block text-gray-300 hover:text-white py-2 transition-colors" onClick={closeMobileMenu}>{t('navigation.home')}</Link>
              <Link to="/sobre-mi" className="block text-purple-400 hover:text-purple-300 py-2 transition-colors font-semibold" onClick={closeMobileMenu}>{t('navigation.aboutMe')}</Link>
              <Link to="/metodologia" className="block text-gray-300 hover:text-white py-2 transition-colors" onClick={closeMobileMenu}>{t('navigation.methodology')}</Link>
              <Link to="/casos-estudio" className="block text-gray-300 hover:text-white py-2 transition-colors" onClick={closeMobileMenu}>{t('navigation.caseStudies')}</Link>
              <Link to="/asesoria" className="block text-gray-300 hover:text-white py-2 transition-colors" onClick={closeMobileMenu}>{t('navigation.consulting')}</Link>
              <Link to="/contacto" className="block text-gray-300 hover:text-white py-2 transition-colors" onClick={closeMobileMenu}>{t('navigation.contact')}</Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

const SobreMiPage = () => {
  const { t } = useTranslations();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Utilidad segura para arrays
  const asArray = (v) => (Array.isArray(v) ? v : []);

  const milestones = [
    {
      year: t('aboutMe.trajectory.milestones.university.year'),
      title: t('aboutMe.trajectory.milestones.university.title'),
      company: t('aboutMe.trajectory.milestones.university.company'),
      description: t('aboutMe.trajectory.milestones.university.description'),
      insight: t('aboutMe.trajectory.milestones.university.insight'),
      icon: "🎓",
      color: "from-blue-500 to-cyan-500",
      isRecent: false
    },
    {
      year: t('aboutMe.trajectory.milestones.equilicua.year'),
      title: t('aboutMe.trajectory.milestones.equilicua.title'),
      company: t('aboutMe.trajectory.milestones.equilicua.company'),
      description: t('aboutMe.trajectory.milestones.equilicua.description'),
      insight: t('aboutMe.trajectory.milestones.equilicua.insight'),
      icon: "💡",
      color: "from-purple-500 to-pink-500",
      isRecent: false
    },
    {
      year: t('aboutMe.trajectory.milestones.emburse.year'),
      title: t('aboutMe.trajectory.milestones.emburse.title'),
      company: t('aboutMe.trajectory.milestones.emburse.company'),
      description: t('aboutMe.trajectory.milestones.emburse.description'),
      insight: t('aboutMe.trajectory.milestones.emburse.insight'),
      icon: "📊",
      color: "from-orange-500 to-red-500",
      isRecent: false
    },
    {
      year: t('aboutMe.trajectory.milestones.powermba.year'),
      title: t('aboutMe.trajectory.milestones.powermba.title'),
      company: t('aboutMe.trajectory.milestones.powermba.company'),
      description: t('aboutMe.trajectory.milestones.powermba.description'),
      insight: t('aboutMe.trajectory.milestones.powermba.insight'),
      icon: "🎯",
      color: "from-green-500 to-emerald-500",
      isRecent: true
    },
    {
      year: t('aboutMe.trajectory.milestones.pandemia.year'),
      title: t('aboutMe.trajectory.milestones.pandemia.title'),
      company: t('aboutMe.trajectory.milestones.pandemia.company'),
      description: t('aboutMe.trajectory.milestones.pandemia.description'),
      insight: t('aboutMe.trajectory.milestones.pandemia.insight'),
      icon: "❤️",
      color: "from-pink-500 to-rose-500",
      isRecent: false
    },
    {
      year: t('aboutMe.trajectory.milestones.beself.year'),
      title: t('aboutMe.trajectory.milestones.beself.title'),
      company: t('aboutMe.trajectory.milestones.beself.company'),
      description: t('aboutMe.trajectory.milestones.beself.description'),
      insight: t('aboutMe.trajectory.milestones.beself.insight'),
      icon: "🌍",
      color: "from-indigo-500 to-purple-500",
      isRecent: true
    },
    {
      year: t('aboutMe.trajectory.milestones.yourbrandvalue.year'),
      title: t('aboutMe.trajectory.milestones.yourbrandvalue.title'),
      company: t('aboutMe.trajectory.milestones.yourbrandvalue.company'),
      description: t('aboutMe.trajectory.milestones.yourbrandvalue.description'),
      insight: t('aboutMe.trajectory.milestones.yourbrandvalue.insight'),
      icon: "🚀",
      color: "from-yellow-500 to-orange-500",
      isRecent: true
    }
  ];

  const recentMilestones = milestones.filter(milestone => milestone.isRecent);
  const olderMilestones = milestones.filter(milestone => !milestone.isRecent);

  const [showFullTimeline, setShowFullTimeline] = React.useState(false);

  const toggleTimeline = () => {
    setShowFullTimeline(!showFullTimeline);
  };

  const skills = [
    { name: t('aboutMe.skills.skillsList.humanizedMarketing'), level: 95, color: "from-purple-500 to-pink-500" },
    { name: t('aboutMe.skills.skillsList.brandStrategy'), level: 92, color: "from-blue-500 to-cyan-500" },
    { name: t('aboutMe.skills.skillsList.ecommerceGrowth'), level: 90, color: "from-green-500 to-emerald-500" },
    { name: t('aboutMe.skills.skillsList.leadershipCommunication'), level: 94, color: "from-orange-500 to-red-500" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <Navigation />
      
      {/* Header */}
      <section className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/" 
            className="inline-flex items-center text-purple-400 hover:text-purple-300 transition-colors duration-300 mb-8"
          >
            <span className="mr-2">←</span>
            {t('home.calculator.backHome')}
          </Link>

          <div className="text-center mb-16">
            <h1 className="text-5xl font-bold text-white mb-6">
              {t('aboutMe.hero.title')} <span className="text-purple-400">{t('aboutMe.hero.titleHighlight')}</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              {t('aboutMe.hero.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Mi Historia */}
      <section className="py-16 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Columna Izquierda - Imagen */}
            <div className="flex justify-center lg:justify-start">
              <div className="relative">
                <img
                  src="/images/tamara-professional.jpg"
                  alt="Tamara Navarrete sonriendo con blazer amarillo"
                  loading="lazy"
                  width={400}
                  height={500}
                  className="w-80 h-96 lg:w-96 lg:h-[28rem] object-cover rounded-2xl shadow-2xl ring-4 ring-purple-500/30 hover:ring-purple-400/50 transition-all duration-300"
                  style={{ objectFit: 'cover', objectPosition: 'center 25%' }}
                  onError={(e) => {
                    console.error('Error loading professional image:', e);
                    e.currentTarget.style.display = 'none';
                  }}
                />
                <div className="absolute -bottom-4 -right-4 w-20 h-20 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-center text-2xl shadow-lg">
                  💼
                </div>
              </div>
            </div>

            {/* Columna Derecha - Biografía */}
            <div className="bg-slate-800/30 backdrop-blur-sm rounded-2xl border border-purple-500/20 p-8">
              <h2 className="text-3xl font-bold text-white mb-6">{t('aboutMe.biography.title')}</h2>

              <div className="space-y-6 text-gray-300 leading-relaxed">
                <p>
                  <strong className="text-white">{t('aboutMe.biography.description1')}</strong>
                </p>
                <p>
                  {t('aboutMe.biography.description2')}
                </p>
                <p>
                  {t('aboutMe.biography.description3')}
                </p>
                <p className="text-purple-300 font-medium">
                  {t('aboutMe.biography.quote')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mi Enfoque Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-green-600/20 to-emerald-600/20 backdrop-blur-sm rounded-2xl border border-green-500/30 p-8">
              <h3 className="text-2xl font-bold text-white mb-8 text-center">🎯 {t('aboutMe.approach.title')}</h3>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                    1
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-white mb-2">{t('aboutMe.approach.deepAnalysis.title')}</h4>
                    <p className="text-gray-300 text-sm">
                      {t('aboutMe.approach.deepAnalysis.description')}
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                    2
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-white mb-2">{t('aboutMe.approach.personalizedStrategy.title')}</h4>
                    <p className="text-gray-300 text-sm">
                      {t('aboutMe.approach.personalizedStrategy.description')}
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                    3
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-white mb-2">{t('aboutMe.approach.measurableResults.title')}</h4>
                    <p className="text-gray-300 text-sm">
                      {t('aboutMe.approach.measurableResults.description')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline Optimizado */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-6">{t('aboutMe.trajectory.title')}</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              {t('aboutMe.trajectory.subtitle')}
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            {/* Hitos Recientes - Siempre Visibles */}
            <div className="space-y-6">
              {recentMilestones.map((milestone, index) => (
                <div key={`recent-${index}`} className="flex items-center space-x-6 bg-slate-800/30 backdrop-blur-sm rounded-xl border border-purple-500/20 p-6 hover:bg-slate-800/40 transition-all duration-300">
                  {/* Icono */}
                  <div className={`flex-shrink-0 w-14 h-14 bg-gradient-to-r ${milestone.color} rounded-full flex items-center justify-center text-xl shadow-lg`}>
                    {milestone.icon}
                  </div>
                  
                  {/* Contenido */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-lg font-bold text-white truncate">{milestone.title}</h3>
                      <span className="text-sm font-semibold text-purple-400 ml-4 flex-shrink-0">{milestone.year}</span>
                    </div>
                    
                    <p className="text-gray-300 text-sm leading-relaxed">
                      {milestone.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Hitos Anteriores - Expandibles */}
            <div className={`space-y-6 mt-6 transition-all duration-500 ease-in-out ${showFullTimeline ? 'max-h-none opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}>
              {olderMilestones.map((milestone, index) => (
                <div key={`older-${index}`} className="flex items-center space-x-6 bg-slate-800/20 backdrop-blur-sm rounded-xl border border-gray-500/10 p-6 hover:bg-slate-800/30 transition-all duration-300">
                  {/* Icono */}
                  <div className={`flex-shrink-0 w-14 h-14 bg-gradient-to-r ${milestone.color} rounded-full flex items-center justify-center text-xl shadow-lg`}>
                    {milestone.icon}
                  </div>
                  
                  {/* Contenido */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-lg font-bold text-white truncate">{milestone.title}</h3>
                      <span className="text-sm font-semibold text-purple-400 ml-4 flex-shrink-0">{milestone.year}</span>
                    </div>
                    
                    <p className="text-gray-300 text-sm leading-relaxed">
                      {milestone.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Botón Ver más/menos */}
            <div className="text-center mt-8">
              <button
                onClick={toggleTimeline}
                className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
              >
                <span className="mr-2">
                  {showFullTimeline ? '↑' : '↓'}
                </span>
                {showFullTimeline ? t('aboutMe.trajectory.viewLess') : t('aboutMe.trajectory.viewMore')}
                <span className="ml-2">
                  {showFullTimeline ? '↑' : '↓'}
                </span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Skills & Expertise */}
      <section className="py-16 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">{t('aboutMe.skills.title')}</h2>
            <p className="text-xl text-gray-300">
              {t('aboutMe.skills.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="bg-slate-800/30 backdrop-blur-sm rounded-2xl border border-purple-500/20 p-8">
              <h3 className="text-2xl font-bold text-white mb-8">💪 {t('aboutMe.skills.technicalStrengths')}</h3>
              <div className="space-y-6">
                {skills.map((skill, index) => (
                  <div key={index}>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-300 font-semibold">{skill.name}</span>
                      <span className="text-white font-bold">{skill.level}%</span>
                    </div>
                    <div className="w-full bg-slate-600 rounded-full h-3">
                      <div 
                        className={`h-3 rounded-full bg-gradient-to-r ${skill.color} transition-all duration-1000`}
                        style={{ width: `${skill.level}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-slate-800/30 backdrop-blur-sm rounded-2xl border border-purple-500/20 p-8">
              <h3 className="text-2xl font-bold text-white mb-8">{t('aboutMe.achievements.title')}</h3>
              <div className="space-y-6">
                {(() => {
                  // Lectura segura de achievements con returnObjects
                  const achievementsRaw = t('aboutMe.achievements.items', { returnObjects: true, defaultValue: [] });
                  const achievements = asArray(achievementsRaw);
                  
                  return achievements.map((achievement, index) => (
                    <div key={index} className="flex items-start space-x-4">
                      <span className="text-yellow-400 text-2xl">{achievement.icon}</span>
                      <div>
                        <h4 className="font-bold text-white">{achievement.title}</h4>
                        <p className="text-gray-400 text-sm">{achievement.institution}</p>
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonios */}
      <section className="py-16 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-6">{t('aboutMe.testimonials.title')}</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-slate-800/30 backdrop-blur-sm rounded-2xl border border-purple-500/20 p-8 text-center">
              <div className="text-4xl mb-4">💡</div>
              <blockquote className="text-gray-300 text-lg italic mb-4">
                "{t('aboutMe.testimonials.testimonial1')}"
              </blockquote>
            </div>
            
            <div className="bg-slate-800/30 backdrop-blur-sm rounded-2xl border border-purple-500/20 p-8 text-center">
              <div className="text-4xl mb-4">🎯</div>
              <blockquote className="text-gray-300 text-lg italic mb-4">
                "{t('aboutMe.testimonials.testimonial2')}"
              </blockquote>
            </div>
            
            <div className="bg-slate-800/30 backdrop-blur-sm rounded-2xl border border-purple-500/20 p-8 text-center">
              <div className="text-4xl mb-4">🚀</div>
              <blockquote className="text-gray-300 text-lg italic mb-4">
                "{t('aboutMe.testimonials.testimonial3')}"
              </blockquote>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-gradient-to-r from-purple-600/20 to-pink-600/20 backdrop-blur-sm rounded-2xl border border-purple-500/30 p-12">
            <h2 className="text-3xl font-bold text-white mb-4">{t('aboutMe.cta.title')}</h2>
            <p className="text-xl text-gray-300 mb-8">
              {t('aboutMe.cta.subtitle')}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <CalendlyButton 
                text={t('aboutMe.cta.bookSession')}
                variant="primary"
                size="large"
                showMicrocopy={false}
              />
              <Link
                to="/asesoria"
                className="border-2 border-purple-500 text-purple-400 px-8 py-4 rounded-xl font-bold text-lg hover:bg-purple-500 hover:text-white transition-all duration-300"
              >
                {t('aboutMe.cta.learnMore')}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SobreMiPage;